<template>
  <div class="faq-page">
    <div v-if="isPageLoading" class="faq-page__loader-wrapper">
      <LoadingSpinner />
    </div>
    <div class="faq-page__content">
      <div class="faq-page__pill">Troubleshooting and FAQs</div>
      <div class="faq-page__title">What can we help you find?</div>
      <div class="faq-page__search">
        <div class="faq-page__search__input-wrapper">
          <SearchIcon
            class="faq-page__search__input-wrapper__search-icon"
            size="24"
          />
          <input v-model="searchQuery" placeholder="Search..." />
          <XIcon
            v-show="searchQuery"
            class="faq-page__search__input-wrapper__x-icon"
            size="16"
            @click="searchQuery = ''"
          />
        </div>
      </div>
      <div
        v-if="!(filteredFaqs && filteredFaqs.length)"
        class="faq-page__no-results"
      >
        <img
          class="faq-page__no-results-wrapper__img"
          src="@/assets/images/status/fail-low.png"
          alt="NO RESULTS"
        />
        <h5 class="faq-page__no-results-wrapper__error">
          Oops! We couldn't find any results matching your search.
        </h5>
        <p class="faq-page__no-results-wrapper__instruction">
          Please try searching for something else.
        </p>
      </div>
      <div v-else class="faq-page__results">
        <FaqCategory
          v-for="faq in filteredFaqs"
          :key="faq.id"
          :faqCategory="faq"
          :searchQuery="searchQuery"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { SearchIcon, XIcon } from 'vue-feather-icons'
import LoadingSpinner from '@/components/loaders/LoadingSpinner.vue'
import FaqCategory from '@/components/faqs/FaqCategory.vue'

export default {
  name: 'FaqPage',
  components: {
    LoadingSpinner,
    FaqCategory,
    XIcon,
    SearchIcon,
  },
  metaInfo() {
    return {
      title: 'INE - FAQ',
    }
  },
  data: () => {
    return {
      originalFaq: null,
      searchQuery: '',
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isPageLoading: state => state.app.isPageLoading,
      currentFaqs: state => state.faqs.currentFaqs,
    }),
    filteredFaqs() {
      // return the original faq if there is no search query
      // or if the search query is empty
      // otherwise, return the filtered faq
      // based on the search query that could be present as a part of any faq value in these fields: name, description_html and short_description.
      // search in any depth of the object and return only the matched objects
      if (!this.searchQuery) {
        return this.originalFaq
      } else {
        return this.originalFaq.filter(faq => {
          return faq.content?.some(faqItem => {
            return (
              faqItem?.title
                ?.toLowerCase()
                .includes(this.searchQuery.toLowerCase()) ||
              faqItem?.text_html
                ?.toLowerCase()
                .includes(this.searchQuery.toLowerCase()) ||
              faqItem.content?.some(faqItem => {
                return (
                  faqItem?.title
                    ?.toLowerCase()
                    .includes(this.searchQuery.toLowerCase()) ||
                  faqItem?.text_html
                    ?.toLowerCase()
                    .includes(this.searchQuery.toLowerCase())
                )
              })
            )
          })
        })
      }
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.name !== from.params.name) {
        this.queryData()
      }
    },
  },
  async created() {
    if (!this.isAuthenticated) {
      this.$router.push({ name: 'login' })
    } else {
      await this.queryData()
      const params = new URLSearchParams(window.location.search)
      if (params.get('search')) {
        this.searchQuery = params.get('search')
      }
    }
  },
  methods: {
    ...mapActions({
      getAllFaqs: 'faqs/getAllFaqs',
    }),
    async queryData() {
      this.$store.dispatch('app/setIsPageLoading', true)
      this.originalFaq = await this.getAllFaqs()
      this.$store.dispatch('app/setIsPageLoading', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.faq-page {
  padding-top: 100px;
  padding-bottom: 60px;
  &__title {
    color: var(--white);
    font-size: 20px;
    line-height: 26px;
    font-style: normal;
    font-weight: 700;
    text-align: center;
    margin: 0 auto;
    @include mobile {
      font-size: 40px;
      line-height: 52px;
    }
    @include tablet {
      font-size: 54px;
      line-height: 66px; /* 122.222% */
      margin-bottom: 40px;
    }
  }
  &__pill {
    color: var(--white);
    margin: 0 auto;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 450;
    line-height: 14px; /* 116.667% */
    display: flex;
    padding: 5px 8px;
    gap: 10px;
    border-radius: 30px;
    background: var(
      --Secondary-Promo-Purple,
      linear-gradient(90deg, #863ad2 3.58%, #5936e4 97.14%)
    );
    box-shadow: 0px 0px 10px 0px rgba(167, 147, 246, 0.5);
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 15px;
    @include mobile {
      padding: 0 50px;
    }
  }
  &__search {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding-bottom: 20px;
    @include mobile {
      flex-direction: column;
      gap: 10px;
      padding-bottom: 30px;
    }
    @include tablet {
      padding-bottom: 40px;
    }
    &__input-wrapper {
      display: flex;
      width: 100%;
      align-items: center;
      flex: 1;
      padding: 0 10px;
      gap: 5px;
      border-radius: 4px;
      background: var(--white-90);
      &:hover {
        background: var(--white);
      }
      &:focus-within {
        box-shadow: var(--purple-dark-box-shadow);
        background: var(--white);

        svg {
          color: var(--butterfly-bush);
        }
      }
      &__x-icon {
        cursor: pointer;
      }
      svg {
        color: var(--dove-gray);
      }
      input {
        flex: 1;
        background: transparent;
        border: none;
        height: 45px;
        font-family: var(--font-book);
        font-style: normal;
        font-weight: 450;
        font-size: rem(12px);
        line-height: rem(14px);
      }
    }
  }
  &__results {
    gap: 20px;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  &__no-results {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-top: 70px;
  }
  &__no-results-wrapper {
    display: flex;
    align-items: center;
    flex-flow: column;
    padding: 20px;
    min-height: 100vh;

    @include desktop {
      grid-row: 4;
      grid-column: 1 / 3;
    }
    &__img {
      width: 280px;
    }
    &__error {
      margin: -30px 0 0 0;
      color: var(--white);
      font-family: var(--font-bold);
      font-style: normal;
      font-weight: 700;
      font-size: rem(20px);
      line-height: rem(26px);
    }
    &__instruction {
      color: var(--light-blue-gray);
      font-family: var(--font-book);
      font-style: normal;
      font-weight: 450;
      font-size: rem(16px);
      line-height: rem(24px);
    }
  }
}
</style>
